import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = "choice" | "call" | "form-name" | "form-phone" | "form-source" | "success";

const referralSources = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "google", label: "Google" },
  { id: "venner", label: "Venner" },
  { id: "andet", label: "Andet" },
];

const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const [step, setStep] = useState<Step>("choice");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedSource, setSelectedSource] = useState<string | null>(null);

  const resetAndClose = () => {
    setTimeout(() => {
      setStep("choice");
      setName("");
      setPhone("");
      setSelectedSource(null);
    }, 300);
    onClose();
  };

  const handleSubmit = () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Udfyld venligst alle felter");
      return;
    }
    setStep("success");
    setTimeout(() => {
      toast.success("Tak! Vi ringer dig op hurtigst muligt.");
      resetAndClose();
    }, 2000);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      resetAndClose();
    }
  };

  const goBack = () => {
    switch (step) {
      case "call":
      case "form-name":
        setStep("choice");
        break;
      case "form-phone":
        setStep("form-name");
        break;
      case "form-source":
        setStep("form-phone");
        break;
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
    }),
  };

  const getDirection = (currentStep: Step): number => {
    const steps: Step[] = ["choice", "call", "form-name", "form-phone", "form-source", "success"];
    return 1; // Always forward for simplicity
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={handleOverlayClick}
        >
          {/* Overlay */}
          <motion.div 
            className="absolute inset-0 bg-foreground/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-sm bg-background rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border/50">
              {step !== "choice" && step !== "success" ? (
                <button
                  onClick={goBack}
                  className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
                  aria-label="Tilbage"
                >
                  <ArrowLeft className="h-5 w-5 text-muted-foreground" />
                </button>
              ) : (
                <div className="w-9" />
              )}
              
              <span className="text-sm font-medium text-muted-foreground">
                {step === "choice" && "Kontakt"}
                {step === "call" && "Ring til os"}
                {step === "form-name" && "Dit navn"}
                {step === "form-phone" && "Dit nummer"}
                {step === "form-source" && "Næsten færdig"}
                {step === "success" && "Sendt!"}
              </span>
              
              <button
                onClick={resetAndClose}
                className="p-2 -mr-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Luk"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            {/* Content */}
            <div className="relative overflow-hidden min-h-[320px]">
              <AnimatePresence mode="wait" custom={getDirection(step)}>
                {/* Step: Choice */}
                {step === "choice" && (
                  <motion.div
                    key="choice"
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="p-8 space-y-4"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Hvordan vil du kontaktes?
                      </h2>
                      <p className="text-muted-foreground text-sm">
                        Vælg den måde der passer dig bedst
                      </p>
                    </div>

                    <button
                      onClick={() => setStep("call")}
                      className="w-full p-5 rounded-2xl border border-border bg-background hover:bg-muted/50 hover:border-primary/30 transition-all group flex items-center gap-4"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                      <div className="text-left flex-1">
                        <span className="block font-medium text-foreground">Ring nu</span>
                        <span className="text-sm text-muted-foreground">Tal med os med det samme</span>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </button>

                    <button
                      onClick={() => setStep("form-name")}
                      className="w-full p-5 rounded-2xl border border-border bg-background hover:bg-muted/50 hover:border-primary/30 transition-all group flex items-center gap-4"
                    >
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center group-hover:bg-accent/30 transition-colors">
                        <svg className="h-5 w-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div className="text-left flex-1">
                        <span className="block font-medium text-foreground">Bliv ringet op</span>
                        <span className="text-sm text-muted-foreground">Vi kontakter dig hurtigst muligt</span>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </button>
                  </motion.div>
                )}

                {/* Step: Call */}
                {step === "call" && (
                  <motion.div
                    key="call"
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="p-8 text-center"
                  >
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                      <Phone className="h-8 w-8 text-primary" />
                    </div>
                    
                    <p className="text-muted-foreground text-sm mb-3">
                      Ring til os på
                    </p>
                    <a 
                      href="tel:+4593889029"
                      className="text-3xl font-semibold text-foreground hover:text-primary transition-colors block mb-8"
                    >
                      93 88 90 29
                    </a>

                    <Button 
                      size="lg" 
                      className="w-full h-14 text-base font-medium rounded-2xl"
                      asChild
                    >
                      <a href="tel:+4593889029">
                        <Phone className="mr-2 h-5 w-5" />
                        Ring op
                      </a>
                    </Button>
                    
                    <p className="text-xs text-muted-foreground mt-6">
                      Hverdage 8:00 – 17:00
                    </p>
                  </motion.div>
                )}

                {/* Step: Form - Name */}
                {step === "form-name" && (
                  <motion.div
                    key="form-name"
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="p-8"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Hvad hedder du?
                      </h2>
                      <p className="text-muted-foreground text-sm">
                        Så vi ved hvem vi taler med
                      </p>
                    </div>

                    <Input
                      type="text"
                      placeholder="Dit fulde navn"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-14 text-base rounded-2xl border-border/50 focus:border-primary text-center"
                      autoFocus
                    />

                    <Button 
                      size="lg" 
                      className="w-full h-14 text-base font-medium rounded-2xl mt-6"
                      onClick={() => name.trim() && setStep("form-phone")}
                      disabled={!name.trim()}
                    >
                      Fortsæt
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </motion.div>
                )}

                {/* Step: Form - Phone */}
                {step === "form-phone" && (
                  <motion.div
                    key="form-phone"
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="p-8"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Dit telefonnummer
                      </h2>
                      <p className="text-muted-foreground text-sm">
                        Så vi kan ringe dig op
                      </p>
                    </div>

                    <Input
                      type="tel"
                      placeholder="12 34 56 78"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="h-14 text-base rounded-2xl border-border/50 focus:border-primary text-center"
                      autoFocus
                    />

                    <Button 
                      size="lg" 
                      className="w-full h-14 text-base font-medium rounded-2xl mt-6"
                      onClick={() => phone.trim() && setStep("form-source")}
                      disabled={!phone.trim()}
                    >
                      Fortsæt
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </motion.div>
                )}

                {/* Step: Form - Source */}
                {step === "form-source" && (
                  <motion.div
                    key="form-source"
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="p-8"
                  >
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Hvor hørte du om os?
                      </h2>
                      <p className="text-muted-foreground text-sm">
                        Valgfrit, men hjælper os meget
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-2 mb-8">
                      {referralSources.map((source) => (
                        <button
                          key={source.id}
                          type="button"
                          onClick={() => setSelectedSource(
                            selectedSource === source.id ? null : source.id
                          )}
                          className={cn(
                            "px-4 py-2.5 rounded-full text-sm font-medium transition-all",
                            selectedSource === source.id
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                          )}
                        >
                          {source.label}
                        </button>
                      ))}
                    </div>

                    <Button 
                      size="lg" 
                      className="w-full h-14 text-base font-medium rounded-2xl"
                      onClick={handleSubmit}
                    >
                      Send
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </motion.div>
                )}

                {/* Step: Success */}
                {step === "success" && (
                  <motion.div
                    key="success"
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="p-8 text-center flex flex-col items-center justify-center min-h-[320px]"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6"
                    >
                      <Check className="h-10 w-10 text-green-600" />
                    </motion.div>
                    
                    <h2 className="text-2xl font-semibold text-foreground mb-2">
                      Tak, {name.split(' ')[0]}!
                    </h2>
                    <p className="text-muted-foreground">
                      Vi ringer dig op hurtigst muligt
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
